import { ReactNode, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import {
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { Stack } from 'src/core/ui';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { PurchaseOrderTable } from 'src/tables/PurchaseTable';
import { PurchaseOrderTableProps } from 'src/tables/PurchaseTable/PurchaseTable.types';
import { PurchaseOrderTableV2 } from 'src/tables/PurchaseTable/PurchaseTableV2';
import {
  PurchaseOrderTicketGroup,
  PurchaseViewMode,
} from 'src/WebApiController';

type PurchaseFlattenedViewProps = {
  before?: ReactNode;
  topListItemBefore?: ReactNode;
} & Pick<
  PurchaseOrderTableProps,
  | 'alwaysShowCheckbox'
  | 'showSelectAllOnHeader'
  | 'enableColumnFilters'
  | 'ignoreMaxCount'
> & {
    useTableV2?: boolean;
    /**
     * Only used when useTableV2 is true.
     * If true, the pagination will be handled by the server.
     * This means we have to have pagination (disablePagination will be overridden to false)
     * and we'll listen to pagination and sorting changes and pass them back to the server.
     *
     * requires `onPaginationChange`, `onSortingChange`, and `serverSidePageIndex` to be provided.
     */
    serverSidePagination?: boolean;
    pageSize?: number | null;
  } & Pick<
    PurchaseOrderTableProps,
    'onPaginationChange' | 'onSortingChange' | 'serverSidePageIndex'
  >;

export const PurchaseFlattenedView = ({
  before,
  topListItemBefore,
  useTableV2 = false,
  serverSidePagination = false,
  pageSize,
  serverSidePageIndex,
  ...rest
}: PurchaseFlattenedViewProps) => {
  const {
    allPurchasesQuery,
    allPurchasesFiltered,
    purchaseCount,
    allPurchasesSummary,
  } = usePurchaseDataContext();
  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();

  const hasTableTotalsFeature = useUserHasTableTotalsFeature();

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (!isEqual(allGroupsIds, [NO_GROUP_ID])) {
      setAllGroupsIds([NO_GROUP_ID]);
    }
  }, [allGroupsIds, setAllGroupsIds]);

  const purchases = useMemo(
    () =>
      (allPurchasesFiltered ?? []).map((p) => p as PurchaseOrderTicketGroup),
    [allPurchasesFiltered]
  );

  const entitiesAggregated = useMemo(
    () =>
      hasTableTotalsFeature && allPurchasesSummary != null
        ? (allPurchasesSummary as PurchaseOrderTicketGroup)
        : undefined,
    [hasTableTotalsFeature, allPurchasesSummary]
  );

  return (
    <Stack direction="column" height="full" width="full">
      {before}
      {topListItemBefore}
      {useTableV2 ? (
        <PurchaseOrderTableV2
          {...rest}
          groupId={NO_GROUP_ID}
          useVirtuoso
          disablePagination={!serverSidePagination}
          manualPagination={serverSidePagination}
          rowCount={
            serverSidePagination
              ? (allPurchasesQuery.data?.totalItemsCount ?? undefined)
              : undefined
          }
          serverSidePageIndex={serverSidePageIndex}
          entityCount={purchaseCount}
          entities={purchases}
          entitiesSummary={entitiesAggregated}
          getDataFail={Boolean(allPurchasesQuery.error)}
          isItemsLoading={allPurchasesQuery.isLoading}
          viewMode={PurchaseViewMode.FlattenedView}
          pageSize={pageSize ?? undefined}
        />
      ) : (
        <PurchaseOrderTable
          {...rest}
          groupId={NO_GROUP_ID}
          useVirtuoso
          disablePagination
          entityCount={purchaseCount}
          entities={purchases}
          entitiesSummary={entitiesAggregated}
          getDataFail={Boolean(allPurchasesQuery.error)}
          isItemsLoading={allPurchasesQuery.isLoading}
          viewMode={PurchaseViewMode.FlattenedView}
        />
      )}
    </Stack>
  );
};
